/**
 * How long a recording ran and how much audio it produced are measured independently: RecordingState
 * keeps a stopwatch of running time, while msDuration comes from the recorder and is the audio that
 * exists. A system interruption stops capture without stopping that stopwatch, so it outrunning the
 * audio is how we learn seconds were never recorded.
 */

/**
 * Shortfalls at or below this are not worth telling a caregiver about. Measured skew between the
 * two clocks is zero on iOS, Android and web, so this is a judgement about a noticeable loss
 * rather than a guard against drift.
 */
const MIN_MISSING_AUDIO_MS = 5000;

/** Milliseconds of audio the recorder never captured, or 0 when nothing material is missing. */
export const getMissingAudioMs = ({ elapsedMs, msDuration }: { elapsedMs: number; msDuration: number }): number => {
    // Android reports -1 when it cannot read the saved file, which says nothing about missing audio.
    if (msDuration <= 0 || elapsedMs <= 0) {
        return 0;
    }

    const missingMs = elapsedMs - msDuration;
    if (missingMs <= MIN_MISSING_AUDIO_MS) {
        return 0;
    }

    return missingMs;
};

/** The missing stretch as user-facing copy, for example "15 seconds" or "2 minutes". */
export const describeMissingAudio = ({ missingAudioMs }: { missingAudioMs: number }): string => {
    const seconds = Math.round(missingAudioMs / 1000);
    if (seconds < 60) {
        return `${seconds} seconds`;
    }

    const minutes = Math.round(seconds / 60);
    if (minutes === 1) {
        return '1 minute';
    }

    return `${minutes} minutes`;
};
